import VERTC, {
  MirrorType,
  StreamIndex,
  IRTCEngine,
  RoomProfileType,
  onUserJoinedEvent,
  onUserLeaveEvent,
  MediaType,
  LocalStreamStats,
  RemoteStreamStats,
  StreamRemoveReason,
  LocalAudioPropertiesInfo,
  RemoteAudioPropertiesInfo,
  AudioProfileType,
  DeviceInfo,
  AutoPlayFailedEvent,
  PlayerEvent,
  NetworkQuality,
  VideoRenderMode,
  ScreenEncoderConfig,
} from '@volcengine/rtc';
import RTCAIAnsExtension from '@volcengine/rtc/extension-ainr';
import { Message } from '@arco-design/web-react';
import logger from '@/utils/logger';
import { string2tlv } from '@/utils/utils';
import { COMMAND, INTERRUPT_PRIORITY } from '@/utils/handler';
import { rtcErrorHandler } from '@/utils/rtcErrorHandler';

declare global {
  interface Window {
    __FEEDBACK_TOKEN__?: string;
    __FEEDBACK_ROOMID__?: string;
  }
}

export interface IEventListener {
  handleError: (e: { errorCode: any }) => void;
  handleUserJoin: (e: onUserJoinedEvent) => void;
  handleUserLeave: (e: onUserLeaveEvent) => void;
  handleTrackEnded: (e: { kind: string; isScreen: boolean }) => void;
  handleUserPublishStream: (e: { userId: string; mediaType: MediaType }) => void;
  handleUserUnpublishStream: (e: {
    userId: string;
    mediaType: MediaType;
    reason: StreamRemoveReason;
  }) => void;
  handleRemoteStreamStats: (e: RemoteStreamStats) => void;
  handleLocalStreamStats: (e: LocalStreamStats) => void;
  handleLocalAudioPropertiesReport: (e: LocalAudioPropertiesInfo[]) => void;
  handleRemoteAudioPropertiesReport: (e: RemoteAudioPropertiesInfo[]) => void;
  handleAudioDeviceStateChanged: (e: DeviceInfo) => void;
  handleAutoPlayFail: (e: AutoPlayFailedEvent) => void;
  handlePlayerEvent: (e: PlayerEvent) => void;
  handleRoomBinaryMessageReceived: (e: { userId: string; message: ArrayBuffer }) => void;
  handleNetworkQuality: (
    uplinkNetworkQuality: NetworkQuality,
    downlinkNetworkQuality: NetworkQuality
  ) => void;
}

export interface BasicBody {
  app_id: string;
  room_id: string;
  user_id: string;
  token?: string;
}

/**
 * @brief RTC Core Client
 * @notes Refer to official website documentation to get more information about the API.
 */
export class RTCClient {
  engine!: IRTCEngine;

  basicInfo!: BasicBody;

  private _audioCaptureDevice?: string;

  private _videoCaptureDevice?: string;

  // 记录当前注册的事件监听器，便于精准解绑
  private registeredEventHandlers: IEventListener | null = null;

  audioBotEnabled = false;

  audioBotStartTime = 0;

  lastRoomId?: string;

  // 添加连接状态检查的安全方法
  private isEngineReady(): boolean {
    return !!(this.engine && typeof this.engine === 'object' && this.engine !== null);
  }

  // 安全的连接状态获取方法
  getConnectionStateSafely(): string {
    return rtcErrorHandler.getConnectionStateSafely(this.engine);
  }

  // 安全的引擎状态检查
  private checkEngineState(): boolean {
    if (!this.isEngineReady()) {
      logger.warn('[RTC] 引擎未初始化或已销毁');
      return false;
    }
    return true;
  }

  createEngine = async () => {
    if (!this.basicInfo || !this.basicInfo.app_id) {
      throw new Error('RtcClient.basicInfo 未设置或 app_id 为空，请先设置 RTC 配置');
    }

    if (process.env.NODE_ENV === 'development') {
      logger.info('[RTC] 创建 RTC 引擎，配置已加载 (敏感信息已隐藏)');
    }

    return rtcErrorHandler.safeRTCOperation(async () => {
      this.engine = VERTC.createEngine(this.basicInfo.app_id);

      // 降低 SDK 控制台噪声：关闭 SDK 控制台上报、将 SDK 日志等级降到 error（若支持）
      try {
        // 一些 SDK 提供 setLogFilter/setLogLevel；若无此 API，忽略异常
        // @ts-ignore
        if (typeof (VERTC as any).setLogLevel === 'function') {
          // 0: verbose, 1: info, 2: warning, 3: error — 以 SDK 实际定义为准
          (VERTC as any).setLogLevel(3);
        }
        // @ts-ignore
        if (this.engine && typeof (this.engine as any).setLogLevel === 'function') {
          (this.engine as any).setLogLevel(3);
        }
        // @ts-ignore
        if (this.engine && typeof (this.engine as any).enableConsoleUpload === 'function') {
          (this.engine as any).enableConsoleUpload(false);
        }
      } catch {}

      // 验证引擎创建是否成功
      if (!this.isEngineReady()) {
        throw new Error('RTC 引擎创建失败');
      }

      try {
        const AIAnsExtension = new RTCAIAnsExtension();
        await this.engine.registerExtension(AIAnsExtension);
        AIAnsExtension.enable();
      } catch (error) {
        logger.warn(
          `当前环境不支持 AI 降噪, 此错误可忽略, 不影响实际使用, e: ${(error as any).message}`
        );
      }

      return this.engine;
    }, 'createEngine');
  };

  addEventListeners = ({
    handleError,
    handleUserJoin,
    handleUserLeave,
    handleTrackEnded,
    handleUserPublishStream,
    handleUserUnpublishStream,
    handleRemoteStreamStats,
    handleLocalStreamStats,
    handleLocalAudioPropertiesReport,
    handleRemoteAudioPropertiesReport,
    handleAudioDeviceStateChanged,
    handleAutoPlayFail,
    handlePlayerEvent,
    handleRoomBinaryMessageReceived,
    handleNetworkQuality,
  }: IEventListener) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过事件监听器注册');
      return;
    }

    try {
      // 若已存在旧监听，先解绑再注册，避免重复绑定
      this.unregisterEventListeners();

      this.engine.on(VERTC.events.onError, handleError);
      this.engine.on(VERTC.events.onUserJoined, handleUserJoin);
      this.engine.on(VERTC.events.onUserLeave, handleUserLeave);
      this.engine.on(VERTC.events.onTrackEnded, handleTrackEnded);
      this.engine.on(VERTC.events.onUserPublishStream, handleUserPublishStream);
      this.engine.on(VERTC.events.onUserUnpublishStream, handleUserUnpublishStream);
      this.engine.on(VERTC.events.onRemoteStreamStats, handleRemoteStreamStats);
      this.engine.on(VERTC.events.onLocalStreamStats, handleLocalStreamStats);
      this.engine.on(VERTC.events.onAudioDeviceStateChanged, handleAudioDeviceStateChanged);
      this.engine.on(VERTC.events.onLocalAudioPropertiesReport, handleLocalAudioPropertiesReport);
      this.engine.on(VERTC.events.onRemoteAudioPropertiesReport, handleRemoteAudioPropertiesReport);
      this.engine.on(VERTC.events.onAutoplayFailed, handleAutoPlayFail);
      this.engine.on(VERTC.events.onPlayerEvent, handlePlayerEvent);
      this.engine.on(VERTC.events.onRoomBinaryMessageReceived, handleRoomBinaryMessageReceived);
      this.engine.on(VERTC.events.onNetworkQuality, handleNetworkQuality);

      // 保存已注册监听器引用
      this.registeredEventHandlers = {
        handleError,
        handleUserJoin,
        handleUserLeave,
        handleTrackEnded,
        handleUserPublishStream,
        handleUserUnpublishStream,
        handleRemoteStreamStats,
        handleLocalStreamStats,
        handleLocalAudioPropertiesReport,
        handleRemoteAudioPropertiesReport,
        handleAudioDeviceStateChanged,
        handleAutoPlayFail,
        handlePlayerEvent,
        handleRoomBinaryMessageReceived,
        handleNetworkQuality,
      };
    } catch (error) {
      logger.error('[RTC] 注册事件监听器失败:', error);
      throw error;
    }
  };

  /**
   * 精准解绑当前注册的事件监听器
   */
  private unregisterEventListeners() {
    if (!this.checkEngineState()) return;
    const h = this.registeredEventHandlers;
    if (!h) return;
    try {
      this.engine.off(VERTC.events.onError, h.handleError);
      this.engine.off(VERTC.events.onUserJoined, h.handleUserJoin);
      this.engine.off(VERTC.events.onUserLeave, h.handleUserLeave);
      this.engine.off(VERTC.events.onTrackEnded, h.handleTrackEnded);
      this.engine.off(VERTC.events.onUserPublishStream, h.handleUserPublishStream);
      this.engine.off(VERTC.events.onUserUnpublishStream, h.handleUserUnpublishStream);
      this.engine.off(VERTC.events.onRemoteStreamStats, h.handleRemoteStreamStats);
      this.engine.off(VERTC.events.onLocalStreamStats, h.handleLocalStreamStats);
      this.engine.off(VERTC.events.onAudioDeviceStateChanged, h.handleAudioDeviceStateChanged);
      this.engine.off(
        VERTC.events.onLocalAudioPropertiesReport,
        h.handleLocalAudioPropertiesReport
      );
      this.engine.off(
        VERTC.events.onRemoteAudioPropertiesReport,
        h.handleRemoteAudioPropertiesReport
      );
      this.engine.off(VERTC.events.onAutoplayFailed, h.handleAutoPlayFail);
      this.engine.off(VERTC.events.onPlayerEvent, h.handlePlayerEvent);
      this.engine.off(VERTC.events.onRoomBinaryMessageReceived, h.handleRoomBinaryMessageReceived);
      this.engine.off(VERTC.events.onNetworkQuality, h.handleNetworkQuality);
    } catch (e) {
      logger.warn('[RTC] 精准解绑事件监听器时发生错误:', e);
    } finally {
      this.registeredEventHandlers = null;
    }
  }

  joinRoom = () => {
    if (!this.checkEngineState()) {
      logger.error('[RTC] 引擎未就绪，无法加入房间');
      throw new Error('RTC 引擎未初始化');
    }

    try {
      this.engine.enableAudioPropertiesReport({ interval: 1000 });
      if (process.env.NODE_ENV === 'development') {
        logger.info('[RTC] 用户加入房间 (敏感信息已隐藏)');
      }
      this.engine.joinRoom(
        this.basicInfo.token!,
        `${this.basicInfo.room_id!}`,
        {
          userId: this.basicInfo.user_id!,
          extraInfo: JSON.stringify({
            call_scene: 'RTC-AIGC',
            user_name: this.basicInfo.user_id,
            user_id: this.basicInfo.user_id,
          }),
        },
        {
          isAutoPublish: true,
          isAutoSubscribeAudio: true,
          roomProfileType: RoomProfileType.chat,
        }
      );
      // join 成功后缓存 roomId
      this.lastRoomId = this.basicInfo.room_id;
      logger.info('[RTC] 房间加入成功，缓存roomId:', this.lastRoomId);
    } catch (error) {
      logger.error('[RTC] 加入房间失败:', error);
      throw error;
    }
  };

  leaveRoom = async () => {
    this.audioBotEnabled = false;

    try {
      // 首先移除所有事件监听器，避免在销毁过程中触发回调
      if (this.engine && this.isEngineReady()) {
        try {
          // 优先精准解绑当前注册的监听器
          this.unregisterEventListeners();
          // 兜底：若 SDK 支持无 handler 的 off(event) 以移除所有监听，则尝试一次
          try {
            const events = [
              VERTC.events.onError,
              VERTC.events.onUserJoined,
              VERTC.events.onUserLeave,
              VERTC.events.onTrackEnded,
              VERTC.events.onUserPublishStream,
              VERTC.events.onUserUnpublishStream,
              VERTC.events.onRemoteStreamStats,
              VERTC.events.onLocalStreamStats,
              VERTC.events.onAudioDeviceStateChanged,
              VERTC.events.onLocalAudioPropertiesReport,
              VERTC.events.onRemoteAudioPropertiesReport,
              VERTC.events.onAutoplayFailed,
              VERTC.events.onPlayerEvent,
              VERTC.events.onRoomBinaryMessageReceived,
              VERTC.events.onNetworkQuality,
            ];
            events.forEach((event) => {
              try {
                // @ts-ignore: 兼容 SDK 可能支持的 off(event) 签名
                this.engine.off(event);
              } catch {}
            });
          } catch {}
        } catch (e) {
          logger.warn('[RTC] 移除事件监听器失败:', e);
        }
      }

      // 添加超时控制的离开房间操作
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('离开房间超时')), 5000);
      });

      if (this.engine && this.engine.leaveRoom && this.isEngineReady()) {
        try {
          await Promise.race([this.engine.leaveRoom(), timeoutPromise]);
          logger.info('[RTC] 成功离开房间');
        } catch (error) {
          logger.warn('[RTC] 离开房间操作失败:', error);
        }
      } else {
        logger.warn('[RTC] 引擎未就绪，跳过离开房间操作');
      }
    } catch (error) {
      logger.warn('[RTC] 离开房间失败:', error);
      // 即使离开房间失败，也要继续清理资源
    } finally {
      // 立即清空引擎引用，避免其他方法访问
      const engineToDestroy = this.engine;
      this.engine = null as any;

      // 清理设备引用
      this._audioCaptureDevice = undefined;
      this._videoCaptureDevice = undefined;
      // 彻底清空 RTC 状态
      this.basicInfo = {} as any;
      this.lastRoomId = undefined;

      // 确保资源被正确清理
      try {
        if (engineToDestroy) {
          // 延迟销毁引擎，给足够时间让内部清理完成
          setTimeout(() => {
            try {
              // 检查引擎是否仍然有效
              if (engineToDestroy && typeof engineToDestroy === 'object') {
                VERTC.destroyEngine(engineToDestroy);
                logger.info('[RTC] 引擎已销毁');
              } else {
                logger.warn('[RTC] 引擎已无效，跳过销毁');
              }
            } catch (destroyError) {
              logger.warn('[RTC] 延迟销毁引擎失败:', destroyError);
            }
          }, 200); // 增加延迟时间
        }
      } catch (destroyError) {
        logger.warn('[RTC] 销毁引擎失败:', destroyError);
      }
    }
  };

  checkPermission(): Promise<{
    video: boolean;
    audio: boolean;
  }> {
    return VERTC.enableDevices({
      video: false,
      audio: true,
    });
  }

  /**
   * @brief get the devices
   * @returns
   */
  async getDevices(props?: { video?: boolean; audio?: boolean }): Promise<{
    audioInputs: MediaDeviceInfo[];
    audioOutputs: MediaDeviceInfo[];
    videoInputs: MediaDeviceInfo[];
  }> {
    const { video = false, audio = true } = props || {};
    let audioInputs: MediaDeviceInfo[] = [];
    let audioOutputs: MediaDeviceInfo[] = [];
    let videoInputs: MediaDeviceInfo[] = [];
    const { video: hasVideoPermission, audio: hasAudioPermission } = await VERTC.enableDevices({
      video,
      audio,
    });
    if (audio) {
      const inputs = await VERTC.enumerateAudioCaptureDevices();
      const outputs = await VERTC.enumerateAudioPlaybackDevices();
      audioInputs = inputs.filter((i) => i.deviceId && i.kind === 'audioinput');
      audioOutputs = outputs.filter((i) => i.deviceId && i.kind === 'audiooutput');
      this._audioCaptureDevice = audioInputs.filter((i) => i.deviceId)?.[0]?.deviceId;
      if (hasAudioPermission) {
        if (!audioInputs?.length) {
          Message.error('无麦克风设备, 请先确认设备情况。');
        }
        if (!audioOutputs?.length) {
          Message.error('无扬声器设备, 请先确认设备情况。');
        }
      } else {
        Message.error('暂无麦克风设备权限, 请先确认设备权限授予情况。');
      }
    }
    if (video) {
      videoInputs = await VERTC.enumerateVideoCaptureDevices();
      videoInputs = videoInputs.filter((i) => i.deviceId && i.kind === 'videoinput');
      this._videoCaptureDevice = videoInputs?.[0]?.deviceId;
      if (hasVideoPermission) {
        if (!videoInputs?.length) {
          Message.error('无摄像头设备, 请先确认设备情况。');
        }
      } else {
        Message.error('暂无摄像头设备权限, 请先确认设备权限授予情况。');
      }
    }

    return {
      audioInputs,
      audioOutputs,
      videoInputs,
    };
  }

  startVideoCapture = async (camera?: string) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过启动视频采集');
      return;
    }
    await this.engine.startVideoCapture(camera || this._videoCaptureDevice);
  };

  stopVideoCapture = async () => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过停止视频采集');
      return;
    }
    this.engine.setLocalVideoMirrorType(MirrorType.MIRROR_TYPE_RENDER);
    await this.engine.stopVideoCapture();
  };

  startScreenCapture = async (enableAudio = false) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过启动屏幕共享');
      return;
    }
    await this.engine.startScreenCapture({
      enableAudio,
    });
  };

  stopScreenCapture = async () => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过停止屏幕共享');
      return;
    }
    await this.engine.stopScreenCapture();
  };

  startAudioCapture = async (mic?: string) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过启动音频采集');
      return;
    }
    await this.engine.startAudioCapture(mic || this._audioCaptureDevice);
  };

  stopAudioCapture = async () => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过停止音频采集');
      return;
    }
    await this.engine.stopAudioCapture();
  };

  publishStream = (mediaType: MediaType) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过发布流');
      return;
    }

    try {
      this.engine.publishStream(mediaType);
    } catch (err) {
      logger.error('[RTC] publishStream error:', err);
    }
  };

  unpublishStream = (mediaType: MediaType) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过取消发布流');
      return;
    }

    try {
      this.engine.unpublishStream(mediaType);
    } catch (err) {
      logger.error('[RTC] unpublishStream error:', err);
    }
  };

  publishScreenStream = async (mediaType: MediaType) => {
    try {
      await this.engine.publishScreen(mediaType);
    } catch (err) {
      logger.error('[RTC] publishScreenStream error:', err);
    }
  };

  unpublishScreenStream = async (mediaType: MediaType) => {
    try {
      await this.engine.unpublishScreen(mediaType);
    } catch (err) {
      logger.error('[RTC] unpublishScreenStream error:', err);
    }
  };

  setScreenEncoderConfig = async (description: ScreenEncoderConfig) => {
    await this.engine.setScreenEncoderConfig(description);
  };

  /**
   * @brief 设置业务标识参数
   * @param businessId
   */
  setBusinessId = (businessId: string) => {
    this.engine.setBusinessId(businessId);
  };

  setAudioVolume = (volume: number) => {
    if (!this.checkEngineState()) {
      logger.debug('[RTC] 引擎未就绪，跳过设置音量操作');
      return;
    }
    this.engine.setCaptureVolume(StreamIndex.STREAM_INDEX_MAIN, volume);
    this.engine.setCaptureVolume(StreamIndex.STREAM_INDEX_SCREEN, volume);
  };

  /**
   * @brief 静音/取消静音本地音频流
   */
  muteLocalAudio = (muted: boolean) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过静音操作');
      return;
    }

    try {
      // 检查是否已经加入房间
      if (!this.basicInfo?.room_id) {
        logger.warn('[RTC] 未加入房间，跳过静音操作');
        return;
      }

      // 通过设置音量为0来实现静音效果
      const volume = muted ? 0 : 100; // 0表示静音，100表示正常音量
      this.engine.setCaptureVolume(StreamIndex.STREAM_INDEX_MAIN, volume);
      this.engine.setCaptureVolume(StreamIndex.STREAM_INDEX_SCREEN, volume);
      logger.info(`[RTC] ${muted ? '静音' : '取消静音'}本地麦克风`);
    } catch (error) {
      logger.error('[RTC] 静音操作失败:', error);
      // 不抛出错误，避免影响UI渲染
    }
  };

  /**
   * @brief 设置音质档位
   */
  setAudioProfile = (profile: AudioProfileType) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过设置音质档位操作');
      return;
    }
    this.engine.setAudioProfile(profile);
  };

  /**
   * @brief 切换设备
   */
  switchDevice = (deviceType: MediaType, deviceId: string) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过切换设备操作');
      return;
    }

    if (deviceType === MediaType.AUDIO) {
      this._audioCaptureDevice = deviceId;
      this.engine.setAudioCaptureDevice(deviceId);
    }
    if (deviceType === MediaType.VIDEO) {
      this._videoCaptureDevice = deviceId;
      this.engine.setVideoCaptureDevice(deviceId);
    }
    if (deviceType === MediaType.AUDIO_AND_VIDEO) {
      this._audioCaptureDevice = deviceId;
      this._videoCaptureDevice = deviceId;
      this.engine.setVideoCaptureDevice(deviceId);
      this.engine.setAudioCaptureDevice(deviceId);
    }
  };

  setLocalVideoMirrorType = (type: MirrorType) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过设置视频镜像操作');
      return;
    }
    return this.engine.setLocalVideoMirrorType(type);
  };

  setLocalVideoPlayer = (
    userId: string,
    renderDom?: string | HTMLElement,
    isScreenShare = false,
    renderMode = VideoRenderMode.RENDER_MODE_FILL
  ) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过设置本地视频播放器操作');
      return;
    }
    return this.engine.setLocalVideoPlayer(
      isScreenShare ? StreamIndex.STREAM_INDEX_SCREEN : StreamIndex.STREAM_INDEX_MAIN,
      {
        renderDom,
        userId,
        renderMode,
      }
    );
  };

  /**
   * @brief 移除播放器
   */
  removeVideoPlayer = (userId: string, scope: StreamIndex | 'Both' = 'Both') => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过移除视频播放器操作');
      return;
    }

    let removeScreen = scope === StreamIndex.STREAM_INDEX_SCREEN;
    let removeCamera = scope === StreamIndex.STREAM_INDEX_MAIN;
    if (scope === 'Both') {
      removeCamera = true;
      removeScreen = true;
    }
    if (removeScreen) {
      this.engine.setLocalVideoPlayer(StreamIndex.STREAM_INDEX_SCREEN, { userId });
    }
    if (removeCamera) {
      this.engine.setLocalVideoPlayer(StreamIndex.STREAM_INDEX_MAIN, { userId });
    }
  };

  /**
   * @brief 启用 AIGC
   */
  startAgent = async (scene: string) => {
    // 确保scene和room_id已经初始化
    if (!scene) {
      logger.warn('[RTC] startAgent: scene参数为空，可能需要等待场景初始化');
      return;
    }
    if (!this.basicInfo?.room_id) {
      logger.warn('[RTC] startAgent: roomId参数为空，跳过启动');
      return;
    }
    if (!this.basicInfo?.user_id) {
      logger.warn('[RTC] startAgent: userId参数为空');
      return;
    }

    logger.info('[RTC] startAgent 参数检查通过:', {
      scene,
      hasRoomId: !!this.basicInfo.room_id,
      hasUserId: !!this.basicInfo.user_id,
    });

    if (this.audioBotEnabled) {
      await this.stopAgent(scene, this.basicInfo.room_id);
    }

    // 延迟导入 Apis 以避免循环依赖
    const { default: Apis } = await import('@/app/index');

    await Apis.VoiceChat.StartVoiceChat({
      SceneID: scene,
      roomId: this.basicInfo.room_id, // 传递动态生成的RoomId
      userId: this.basicInfo.user_id, // 传递动态生成的UserId
    });
    this.audioBotEnabled = true;
    this.audioBotStartTime = Date.now();
    logger.info('[RTC] startAgent 调用成功');
  };

  /**
   * @brief 关闭 AIGC
   */
  stopAgent = async (scene: string, roomId?: string) => {
    const useRoomId = roomId || this.lastRoomId;

    // 确保有必要的参数
    if (!scene) {
      logger.warn('[RTC] stopAgent: 缺少scene参数');
      this.audioBotEnabled = false;
      return;
    }

    if (!useRoomId) {
      logger.warn('[RTC] stopAgent: 缺少roomId参数');
      this.audioBotEnabled = false;
      return;
    }

    if (this.audioBotEnabled || sessionStorage.getItem('audioBotEnabled')) {
      try {
        // 延迟导入 Apis 以避免循环依赖
        const { default: Apis } = await import('@/app/index');

        await Apis.VoiceChat.StopVoiceChat({
          SceneID: scene,
          RoomId: useRoomId, // 始终用缓存
        });
        this.audioBotStartTime = 0;
        sessionStorage.removeItem('audioBotEnabled');
      } catch (error) {
        logger.warn('[RTC] stopAgent: StopVoiceChat失败', error);
      }
    }
    this.audioBotEnabled = false;
  };

  /**
   * @brief 命令 AIGC
   */
  commandAgent = ({
    command,
    agentName,
    interruptMode = INTERRUPT_PRIORITY.NONE,
    message = '',
  }: {
    command: COMMAND;
    agentName: string;
    interruptMode?: INTERRUPT_PRIORITY;
    message?: string;
  }) => {
    if (!this.checkEngineState()) {
      logger.warn('[RTC] 引擎未就绪，跳过AIGC命令操作');
      return;
    }

    if (this.audioBotEnabled) {
      this.engine.sendUserBinaryMessage(
        agentName,
        string2tlv(
          JSON.stringify({
            Command: command,
            InterruptMode: interruptMode,
            Message: message,
          }),
          'ctrl'
        )
      );
      return;
    }
    logger.warn('[RTC] Interrupt failed, bot not enabled.');
  };

  /**
   * @brief 更新 AIGC 配置
   */
  updateAgent = async (scene: string) => {
    if (this.audioBotEnabled) {
      await this.stopAgent(scene, this.basicInfo.room_id);
      await this.startAgent(scene);
    } else {
      await this.startAgent(scene);
    }
  };

  /**
   * @brief 获取当前 AI 是否启用
   */
  getAgentEnabled = () => {
    return this.audioBotEnabled;
  };
}

export default new RTCClient();
